import type { HelpGuide } from '../types';

export const docIngestGuide: HelpGuide = {
  key: 'doc-ingest',
  title: 'ตรวจเอกสาร AI (Doc Ingest)',
  subtitle: 'เมนู: เอกสาร AI ▸ ตรวจเอกสาร AI',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/doc-ingest/คู่มือการใช้งาน_ตรวจเอกสาร-AI_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ตรวจเอกสาร AI (Doc Ingest)” คือกล่องรับเอกสารที่ให้ AI ช่วยอ่านใบกำกับภาษี/ใบเสร็จ แล้วสกัดข้อมูล (ผู้ขาย เลขที่ใบกำกับ วันที่ มูลค่า ภาษี รายการสินค้า) ออกมาให้อัตโนมัติ พนักงานบัญชีเพียงตรวจทานความถูกต้องแล้วกดยอมรับ ระบบจะตั้งหนี้ (ใบตั้งหนี้ AP) และลงบัญชีให้เอง ลดการพิมพ์ข้อมูลซ้ำและความผิดพลาดจากการคีย์มือ',
        },
        {
          kind: 'callout',
          text: 'แนวคิดการทำงาน: อัปโหลด/รับเอกสาร → AI สกัดข้อมูล + ตรวจความถูกต้อง + ตรวจสอบผู้ขายกับกรมสรรพากร (RD) → คนตรวจทาน → กดยอมรับ → ระบบตั้งหนี้และลงบัญชี (ถ้าพบความผิดปกติจะส่งให้อนุมัติ 4 ตา ก่อน)',
          variant: 'info',
        },
        {
          kind: 'para',
          text: 'เอกสารเข้าระบบได้หลายช่องทาง:',
        },
        {
          kind: 'bullets',
          items: [
            { lead: 'อัปโหลด — ', text: 'พนักงานลากไฟล์หรือเลือกไฟล์ PDF/รูปภาพในหน้านี้เอง' },
            {
              lead: 'อีเมล — ',
              text: 'ส่งเอกสารเข้าอีเมลรับเอกสารของบริษัท ระบบดึงไฟล์แนบมาอ่านอัตโนมัติ',
            },
            {
              lead: 'สแกน — ',
              text: 'เอกสารจากเครื่องสแกนที่ส่งเข้ากล่องสแกน แล้วประมวลผลรอบกลางคืน',
            },
            { lead: 'LINE — ', text: 'ส่งรูปเอกสารผ่าน LINE เข้าระบบ' },
          ],
        },
        {
          kind: 'image',
          src: '/help/doc-ingest/01-inbox-review.png',
          caption:
            'กล่องรับเอกสาร AI — ปุ่มอัปโหลด, แท็บสถานะ (รอตรวจสอบ/รออนุมัติ/ลงบัญชีแล้ว/ปฏิเสธ), ตารางเอกสาร พร้อมป้ายความเชื่อมั่น/การตรวจสอบ/RD',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'หน้าจอกล่องรับเอกสารแบ่งเป็น 3 ส่วนหลัก:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'พื้นที่อัปโหลด — ',
              text: 'ปุ่ม “อัปโหลดเอกสาร” สำหรับลากไฟล์/เลือกไฟล์ให้ AI อ่าน (ดูหัวข้อ 3)',
            },
            {
              lead: 'แท็บสถานะ — ',
              text: 'รอตรวจสอบ / รออนุมัติ / ลงบัญชีแล้ว / ปฏิเสธ / ล้มเหลว แต่ละแท็บมีจำนวนเอกสารในวงเล็บ',
            },
            {
              lead: 'แถบตัวกรอง — ',
              text: 'ค้นหา เลขที่ใบกำกับ/ผู้ขาย, เลือก ช่องทาง, ช่วงวันที่ (ตั้งแต่วันที่/ถึงวันที่), ผู้ขาย และปุ่มสลับ เรียงตามความรุนแรง กับ RD ผิดปกติ',
            },
            {
              lead: 'ตารางเอกสาร — ',
              text: 'แสดงรายการเอกสารพร้อมป้ายสถานะ คลิกที่แถวเพื่อเปิดหน้าตรวจทานรายเอกสาร',
            },
          ],
        },
        {
          kind: 'para',
          text: 'คอลัมน์ในตาราง: เอกสาร · ผู้ขาย · เลขที่ใบกำกับ · วันที่ · มูลค่ารวม · ความเชื่อมั่น · การตรวจสอบ · RD · ภาษีซื้อ · ช่องทาง · อัปโหลด ในแท็บรอตรวจสอบและรออนุมัติ ระบบยังจัดกลุ่มแถวตามความรุนแรง: ผิดปกติจากกรมสรรพากร (RD) · ข้อผิดพลาดร้ายแรง · มีคำเตือน · พร้อมตรวจ เพื่อให้ตรวจรายการเสี่ยงก่อน',
        },
      ],
    },
    {
      id: 'upload',
      heading: '3. การอัปโหลดเอกสารให้ AI อ่าน',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม “อัปโหลดเอกสาร” แล้วลากไฟล์มาวางในกรอบ หรือคลิกเพื่อเลือกไฟล์ รองรับไฟล์ PDF, PNG, JPG (รูปภาพ) ขนาดไม่เกิน 10 MB ระหว่างที่ระบบทำงานจะขึ้นข้อความ “กำลังอัปโหลดและอ่านเอกสาร…”',
        },
        {
          kind: 'para',
          text: 'เมื่ออ่านเสร็จ เอกสารจะเข้าไปอยู่ในแท็บ รอตรวจสอบ ทันที พร้อมข้อมูลที่ AI สกัดได้ หากอัปโหลดไฟล์เดิมซ้ำ ระบบจะแจ้ง “เอกสารนี้เคยอัปโหลดแล้ว” เพื่อกันการบันทึกซ้ำ',
        },
        {
          kind: 'image',
          src: '/help/doc-ingest/02-upload-dropzone.png',
          caption: 'พื้นที่อัปโหลด — ลากไฟล์มาวางหรือเลือกไฟล์ PDF/PNG/JPG ให้ AI สกัดข้อมูล',
        },
      ],
    },
    {
      id: 'review-tab',
      heading: '4. แท็บ “รอตรวจสอบ” และการตรวจทาน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ รอตรวจสอบ รวมเอกสารที่ AI อ่านเสร็จแล้วและรอคนตรวจ ระบบแสดงตัวนับ “เหลืออีก X ฉบับ” ให้รู้ปริมาณงานในคิว ดูป้ายในแต่ละแถวเพื่อประเมินก่อนเปิด: ความเชื่อมั่น (AI มั่นใจแค่ไหน), การตรวจสอบ (พบปัญหา/คำเตือน/ผ่าน) และ RD (ผลตรวจผู้ขายกับกรมสรรพากร)',
        },
        {
          kind: 'para',
          text: 'คลิกที่แถวเพื่อเปิดหน้าตรวจทานรายเอกสาร ด้านซ้ายเป็นภาพเอกสารต้นฉบับ ด้านขวาเป็นฟอร์มข้อมูลที่ AI สกัดมา ตรวจทานและแก้ไขให้ถูกต้อง เลือกผู้ขาย กำหนดรหัสค่าใช้จ่ายของแต่ละรายการ และเลือกสถานะภาษีซื้อ (ขอคืนได้/ภาษีซื้อค้าง) จากนั้นกด ยอมรับและลงบัญชี หรือ ปฏิเสธเอกสาร (ต้องระบุเหตุผล)',
        },
        {
          kind: 'callout',
          text: 'การกดยอมรับจะตั้งใบตั้งหนี้ (AP) ให้อัตโนมัติ ถ้าไม่มีความผิดปกติจะลงบัญชีทันที (สถานะ ลงบัญชีแล้ว) แต่ถ้าพบความผิดปกติ (เช่น RD ไม่ตรง ความเชื่อมั่นต่ำ หรือมีการแก้ไขข้อมูลสำคัญ) ระบบจะส่งเข้าอนุมัติ 4 ตา ก่อน (สถานะ รออนุมัติ)',
          variant: 'warn',
        },
      ],
    },
    {
      id: 'approval-tab',
      heading: '5. แท็บ “รออนุมัติ” (อนุมัติ 4 ตา)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ รออนุมัติ รวมเอกสารที่ผ่านการตรวจทานแล้ว แต่ระบบตรวจพบความผิดปกติจึงต้องให้ผู้มีอำนาจอนุมัติยืนยันก่อนลงบัญชี (หลักการแยกหน้าที่ maker-checker) เอกสารกลุ่มนี้รอผลการอนุมัติตามเงื่อนไขของบริษัท',
        },
        {
          kind: 'para',
          text: 'เหตุที่ทำให้เอกสารต้องอนุมัติ เช่น ผู้ขายไม่ตรงกับทะเบียนกรมสรรพากร, ความเชื่อมั่นของ AI ต่ำ, พบข้อผิดพลาดร้ายแรงในการตรวจสอบ, มีการเปลี่ยนสถานะภาษีซื้อ หรือมีการแก้ไขข้อมูลสำคัญก่อนยอมรับ เมื่อได้รับอนุมัติครบ ระบบจะลงบัญชีและย้ายไปแท็บ ลงบัญชีแล้ว',
        },
        {
          kind: 'image',
          src: '/help/doc-ingest/03-inbox-approval.png',
          caption:
            'แท็บรออนุมัติ — เอกสารที่ AI สกัดและตรวจทานแล้ว แต่พบความผิดปกติจึงรอการอนุมัติ 4 ตา',
        },
      ],
    },
    {
      id: 'posted-tab',
      heading: '6. แท็บ “ลงบัญชีแล้ว”',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ลงบัญชีแล้ว รวมเอกสารที่ตั้งหนี้และลงบัญชีเรียบร้อย ทั้งที่ลงทันทีหลังยอมรับ และที่ผ่านการอนุมัติ 4 ตา มาแล้ว เอกสารกลุ่มนี้เชื่อมโยงกับใบตั้งหนี้ (AP Bill) และใบสำคัญที่สร้างขึ้น',
        },
        {
          kind: 'para',
          text: 'ใช้แท็บนี้เพื่อย้อนดูเอกสารที่ดำเนินการเสร็จแล้ว และตรวจสอบว่าเอกสารใดถูกตั้งหนี้ไปแล้ว ค้นหาด้วยตัวกรอง เลขที่ใบกำกับ/ผู้ขาย/ช่วงวันที่ ได้เช่นเดียวกับแท็บอื่น',
        },
        {
          kind: 'image',
          src: '/help/doc-ingest/04-inbox-posted.png',
          caption: 'แท็บลงบัญชีแล้ว — เอกสารที่สร้างใบตั้งหนี้และลงบัญชีเรียบร้อยแล้ว',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '7. สรุปแท็บสถานะของเอกสาร',
      blocks: [
        {
          kind: 'table',
          head: ['แท็บ', 'ความหมาย'],
          rows: [
            ['รอตรวจสอบ', 'AI อ่านข้อมูลเสร็จแล้ว รอพนักงานตรวจทานและยอมรับ'],
            ['รออนุมัติ', 'ตรวจทานแล้วแต่พบความผิดปกติ ส่งให้อนุมัติ 4 ตา ก่อนลงบัญชี'],
            ['ลงบัญชีแล้ว', 'ตั้งหนี้และลงบัญชีเรียบร้อยแล้ว'],
            ['ปฏิเสธ', 'พนักงานปฏิเสธเอกสารพร้อมระบุเหตุผล'],
            ['ล้มเหลว', 'AI อ่านเอกสารไม่สำเร็จ หรืออยู่ระหว่างคิวประมวลผล'],
          ],
        },
      ],
    },
    {
      id: 'chips',
      heading: '8. ความหมายของป้ายในตาราง',
      blocks: [
        { kind: 'para', text: 'ป้าย ความเชื่อมั่น — บอกว่า AI มั่นใจในข้อมูลที่อ่านมาแค่ไหน:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ครบถ้วน — ', text: 'AI มั่นใจสูง ข้อมูลครบ ตรวจทานได้เร็ว' },
            { lead: 'ควรตรวจสอบ — ', text: 'มั่นใจปานกลาง ควรตรวจให้แน่ใจ' },
            { lead: 'ต้องตรวจสอบ — ', text: 'มั่นใจต่ำ ต้องตรวจทานละเอียด' },
          ],
        },
        {
          kind: 'para',
          text: 'ป้าย การตรวจสอบ — ผลตรวจความถูกต้องของข้อมูล: มีปัญหา (ร้ายแรง) / มีคำเตือน / ผ่าน',
        },
        {
          kind: 'para',
          text: 'ป้าย RD — ผลตรวจผู้ขายกับทะเบียนกรมสรรพากร: ตรงกับสรรพากร · ไม่ได้จด VAT · เลขภาษีไม่ถูกต้อง · ชื่อไม่ตรง · สาขาไม่ตรง · ตรวจไม่ได้ · ยังไม่ตรวจ',
        },
        {
          kind: 'para',
          text: 'ป้าย ภาษีซื้อ — สถานะการขอคืนภาษีซื้อ: ขอคืนได้ (CLAIMABLE) หรือ ภาษีซื้อค้าง (SUSPENSE)',
        },
      ],
    },
    {
      id: 'filters',
      heading: '9. การค้นหาและกรองเอกสาร',
      blocks: [
        {
          kind: 'para',
          text: 'ใช้แถบตัวกรองด้านบนตารางเพื่อหาเอกสารที่ต้องการ:',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ค้นหา เลขที่ใบกำกับ / ผู้ขาย — ',
              text: 'พิมพ์เลขที่ใบกำกับหรือชื่อผู้ขายเพื่อกรองทันที',
            },
            { lead: 'ช่องทาง — ', text: 'เลือกดูเฉพาะ อัปโหลด / อีเมล / สแกน / LINE หรือทั้งหมด' },
            { lead: 'ตั้งแต่วันที่ / ถึงวันที่ — ', text: 'กรองตามช่วงวันที่ของเอกสาร' },
            { lead: 'ผู้ขาย — ', text: 'กรองด้วยชื่อผู้ขายบางส่วน' },
            {
              lead: 'เรียงตามความรุนแรง — ',
              text: 'จัดเรียงให้เอกสารเสี่ยง (มีปัญหา/คำเตือน) ขึ้นก่อน',
            },
            { lead: 'RD ผิดปกติ — ', text: 'แสดงเฉพาะเอกสารที่ผลตรวจกรมสรรพากรผิดปกติ' },
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '10. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'อัปโหลดแล้วแต่เอกสารไม่ขึ้นในแท็บรอตรวจสอบ?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ถ้าระบบอ่านไฟล์ไม่สำเร็จ เอกสารจะไปอยู่ในแท็บ ล้มเหลว ให้ลองอัปโหลดไฟล์ที่ชัดเจนขึ้น (สแกนคมชัด ไม่เบลอ)',
            },
          ],
        },
        { kind: 'para', text: 'ทำไมบางเอกสารต้องอนุมัติ 4 ตา?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เพราะระบบตรวจพบความผิดปกติ เช่น RD ไม่ตรง ความเชื่อมั่นต่ำ หรือมีการแก้ไขข้อมูลสำคัญ เป็นการป้องกันการตั้งหนี้ที่ผิดพลาด/ทุจริต',
            },
          ],
        },
        { kind: 'para', text: 'อัปโหลดไฟล์เดิมซ้ำได้ไหม?' },
        {
          kind: 'bullets',
          items: [
            { text: 'ระบบจะตรวจจับและแจ้ง “เอกสารนี้เคยอัปโหลดแล้ว” เพื่อกันการตั้งหนี้ซ้ำ' },
          ],
        },
        { kind: 'para', text: 'ป้าย RD “ตรวจไม่ได้” หมายความว่าอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ระบบยังตรวจผู้ขายกับกรมสรรพากรไม่ได้ในขณะนั้น สามารถสั่งตรวจใหม่ได้ในหน้าตรวจทานรายเอกสาร',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '11. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Doc Ingest — ',
              text: 'การรับเอกสารเข้าระบบให้ AI สกัดข้อมูลและตั้งหนี้อัตโนมัติ',
            },
            {
              lead: 'การสกัดข้อมูล (Extract) — ',
              text: 'AI อ่านเอกสารแล้วดึงข้อมูลออกมาเป็นช่อง ๆ (ผู้ขาย/เลขที่/มูลค่า/ภาษี)',
            },
            {
              lead: 'ความเชื่อมั่น (Confidence) — ',
              text: 'ระดับที่ AI มั่นใจในข้อมูลที่อ่าน (ครบถ้วน/ควรตรวจสอบ/ต้องตรวจสอบ)',
            },
            { lead: 'RD Verify — ', text: 'การตรวจสอบผู้ขายกับทะเบียนผู้จด VAT ของกรมสรรพากร' },
            {
              lead: 'ใบตั้งหนี้ (AP Bill) — ',
              text: 'เอกสารตั้งเจ้าหนี้ที่ระบบสร้างเมื่อยอมรับเอกสาร',
            },
            {
              lead: 'อนุมัติ 4 ตา — ',
              text: 'การแยกหน้าที่ผู้ทำกับผู้อนุมัติ (maker-checker) เพื่อควบคุมความเสี่ยง',
            },
            {
              lead: 'ภาษีซื้อขอคืนได้ / ค้าง — ',
              text: 'สถานะภาษีซื้อว่านำไปขอคืนได้ทันที หรือต้องพักไว้ก่อน',
            },
          ],
        },
      ],
    },
  ],
};
